import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import {
  CheckCircleOutline,
  DescriptionOutlined,
  ErrorOutline,
  HelpOutline,
  OpenInNew,
  TableChartOutlined,
} from "@mui/icons-material";
import { useState } from "react";

import PropTypes from "prop-types";
import AssistantText from "src/reusable/Chat/AssistantMessage/AssistantText";
import {
  isTabularDatasetType,
  useDatasetViewerCitationPresentation,
} from "src/reusable/Chat/citations/CitationPresentationContext";
import {
  useGetDataset,
  useGetDatasetType,
  useGetSourceDataRecord,
} from "src/views/datasets/services/DatasetServices";
import { useSubsetDetailedPOCRecords } from "../../cap-trending/trendingServices";
import { getSourceDisplayName } from "src/views/assistant/utils/sourceDisplayName";
import { CitationRevision } from "./CitationRevision";
import { citedRevisionChanged, citedRevisionUnavailable } from "./citationPin";

/**
 * Collapsible markdown justification for an LLM screening prediction.
 * EXPANDED by default: the reasoning is the point of the audit view — a
 * reviewer checking whether a screening is defensible should not have to
 * click to find out why the model chose the label. Collapsing stays
 * available per prediction for scanning a long record.
 */
export const PredictionJustification = ({ justification }) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <div
      className={`llm-justification ${
        expanded ? "llm-justification--expanded" : ""
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
        data-testid="llm-justification-toggle"
      >
        {expanded ? "▾ Hide justification" : "▸ Show justification"}
      </button>
      {expanded && (
        <div
          className="llm-justification__body"
          data-testid="llm-justification-body"
        >
          <AssistantText chatMessage={justification} />
        </div>
      )}
    </div>
  );
};

PredictionJustification.propTypes = {
  justification: PropTypes.string.isRequired,
};

// Exactly the fields LLMReference serialises, plus the server's read-time
// pin_status. It is extra="ignore", so a document title, file name or
// metadata blob never arrives however the reference was produced.
const referenceShape = PropTypes.shape({
  description: PropTypes.string.isRequired,
  quote: PropTypes.string,
  quote_verified: PropTypes.bool,
  dataset_id: PropTypes.number,
  source_uid: PropTypes.string,
  sdr_id: PropTypes.number,
  page_number: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  pin: PropTypes.object,
  pin_status: PropTypes.object,
});

// The model writes `description` as the human-readable locator ("BR-CAP-102
// section 3.0"); it is the only label the payload carries.
const formatReferenceLabel = (reference) => reference.description;

const formatReferenceValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not available";
  }
  return typeof value === "object"
    ? JSON.stringify(value, null, 2)
    : String(value);
};

const formatReferenceLocation = (reference) => {
  const pages = Array.isArray(reference.page_number)
    ? reference.page_number
    : [reference.page_number];
  const visiblePages = pages.filter((page) => page != null).join(", ");
  return visiblePages ? `Page ${visiblePages}` : null;
};

const getTabularPreviewFields = (data) => {
  const preferredKeys = ["title", "description", "cr_id", "UID", "uid"];
  const entries = Object.entries(data || {});
  const preferred = preferredKeys
    .map((key) => entries.find(([entryKey]) => entryKey === key))
    .filter(Boolean);
  return preferred.length ? preferred.slice(0, 2) : entries.slice(0, 2);
};

const TabularEvidenceItem = ({
  reference,
  datasetId,
  onOpen,
  unavailable,
  changed,
}) => {
  const [expanded, setExpanded] = useState(false);
  // No row preview for a deleted record, nor for one edited since it was
  // cited: its current row is not the cited content.
  const previewable = !unavailable && !changed;
  const sourceUid = reference.source_uid;
  // Raw id, negative included: a replicated dataset IS the negative id in the
  // API, and /datasets/{id}/multirecords filters on Dataset.id — abs() 404s,
  // or silently reads a local dataset that happens to hold the positive one.
  // Only viewer ROUTES take the canonical positive id, inside openCitation.
  const { data: dataset, isLoading: isDatasetLoading } = useGetDataset(
    datasetId ?? null,
  );
  const { data: records, isLoading } = useSubsetDetailedPOCRecords(
    datasetId ?? null,
    sourceUid && expanded && previewable ? [sourceUid] : null,
  );
  const record = records?.[0];
  const previewFields = getTabularPreviewFields(record?.data);
  const datasetName = isDatasetLoading
    ? "Loading dataset details..."
    : dataset?.name;

  return (
    <div
      className={`evidence-source ${expanded ? "evidence-source--expanded" : ""}`}
    >
      <div className="evidence-source__capsule-row">
        <button
          type="button"
          className="evidence-capsule"
          onClick={
            previewable ? () => setExpanded((current) => !current) : onOpen
          }
          aria-expanded={previewable ? expanded : undefined}
        >
          <TableChartOutlined fontSize="small" aria-hidden="true" />
          <span className="evidence-capsule__content">
            {datasetName && <strong title={datasetName}>{datasetName}</strong>}
            {/* The locator the model wrote keeps its own line: the dataset or
                document name takes the title line once it loads. */}
            <span
              className="evidence-capsule__locator"
              title={reference.description}
            >
              {reference.description}
            </span>
            <span>{`Tabular record · ${reference.source_uid || "Unknown record"}`}</span>
          </span>
        </button>
        <Tooltip title="Open record">
          <IconButton size="small" onClick={onOpen} aria-label="Open record">
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      {expanded && previewable && (
        <div className="evidence-source__preview">
          {isLoading && (
            <span className="evidence-item__loading">
              <CircularProgress size={14} /> Loading record
            </span>
          )}
          {!isLoading && !record && (
            <span className="evidence-item__note">
              The referenced row is not available in this dataset.
            </span>
          )}
          {record?.data && (
            <>
              <dl className="evidence-item__preview">
                {previewFields.map(([key, value]) => (
                  <div key={key}>
                    <dt>{key.replace(/[_-]+/g, " ")}</dt>
                    <dd>{formatReferenceValue(value)}</dd>
                  </div>
                ))}
              </dl>
              <details className="evidence-item__full-record">
                <summary>Show all record fields</summary>
                <dl className="tabular-reference__grid">
                  {Object.entries(record.data).map(([key, value]) => (
                    <div key={key}>
                      <dt>{key.replace(/[_-]+/g, " ")}</dt>
                      <dd>{formatReferenceValue(value)}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
};

TabularEvidenceItem.propTypes = {
  reference: referenceShape.isRequired,
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  /** The cited revision is gone: never fetch or preview the current row. */
  unavailable: PropTypes.bool,
  /** The record was edited since it was cited: open it, but do not preview
      its current row as the cited content. */
  changed: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
};

const DocumentEvidenceItem = ({
  reference,
  datasetId,
  onOpen,
  unavailable,
}) => {
  // A deleted record would only 404, so its name is never fetched.
  const { data: sourceDataRecord, isLoading: isSourceDataRecordLoading } =
    useGetSourceDataRecord(unavailable ? null : reference.sdr_id);
  // See TabularEvidenceItem: the raw id is the API's id.
  const { data: dataset, isLoading: isDatasetLoading } = useGetDataset(
    datasetId ?? null,
  );
  const isLoading =
    (!unavailable && isSourceDataRecordLoading) || isDatasetLoading;
  const documentName = isLoading
    ? "Loading document details..."
    : getSourceDisplayName(sourceDataRecord?.data) ||
      sourceDataRecord?.data?.file_name;
  const location = formatReferenceLocation(reference);
  const metadata = isLoading
    ? null
    : [dataset?.name, location].filter(Boolean).join(" · ");

  return (
    <button
      type="button"
      className="evidence-capsule evidence-capsule--document"
      onClick={onOpen}
    >
      <DescriptionOutlined fontSize="small" aria-hidden="true" />
      <span className="evidence-capsule__content">
        {documentName && <strong title={documentName}>{documentName}</strong>}
        <span
          className="evidence-capsule__locator"
          title={reference.description}
        >
          {reference.description}
        </span>
        {metadata && <span title={metadata}>{metadata}</span>}
      </span>
      <OpenInNew className="evidence-capsule__open" fontSize="small" />
    </button>
  );
};

DocumentEvidenceItem.propTypes = {
  reference: referenceShape.isRequired,
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onOpen: PropTypes.func.isRequired,
  /** The cited record was deleted: never fetch it. */
  unavailable: PropTypes.bool,
};

/**
 * One reference chip. References with a dataset id deep-link to the dataset
 * viewer / document view (same click-through as chat citations); the dataset
 * type is resolved so tabular rows open the table viewer rather than the
 * document route. References without a dataset id render as plain chips.
 */
const EvidenceItem = ({ reference, citationNumber, onUnavailable }) => {
  // Only the reference's OWN dataset id. A uid with no dataset id was being
  // attributed to the automation's ground-truth dataset and opened as that
  // dataset's record — a guess, presented as provenance.
  const datasetId = reference.dataset_id ?? null;
  const { openCitation } = useDatasetViewerCitationPresentation();
  const { data: datasetType } = useGetDatasetType(datasetId);
  const clickable = !!datasetId;
  const label = formatReferenceLabel(reference);
  // ID-based: TABULAR, ACTIVITY and ATOMRESULTS all store one row per SDR.
  const isTabular = isTabularDatasetType(datasetType?.id);
  // The cited record was deleted, so there is nothing to open.
  const unavailable = citedRevisionUnavailable(reference);
  // Edited in place since it was cited: it opens, but its current row is not
  // the cited content, so it is not previewed as if it were.
  const changed = citedRevisionChanged(reference);
  const open = () =>
    unavailable
      ? onUnavailable()
      : openCitation({
          citationNumber,
          datasetId,
          datasetTypeId: datasetType?.id,
          sourceUid: reference.source_uid ?? undefined,
          sdrId: reference.sdr_id ?? undefined,
          // The quoted passage, when the server could still locate it.
          chunkId: reference.pin_status?.jump_chunk_id ?? undefined,
          // page_number is routinely an ARRAY in chunk metadata (page_span
          // writes [start, end]); wrapping it again nests it, and the citation
          // viewer's pageNumber[0] then yields the inner array — a malformed
          // ?page_number=1,5 anchor that opens the wrong page. Normalize like
          // the two formatters above already do.
          pageNumber:
            reference.page_number != null
              ? Array.isArray(reference.page_number)
                ? reference.page_number
                : [reference.page_number]
              : undefined,
        });

  if (isTabular && reference.source_uid) {
    return (
      <TabularEvidenceItem
        reference={reference}
        datasetId={datasetId}
        onOpen={open}
        unavailable={unavailable}
        changed={changed}
      />
    );
  }

  return clickable ? (
    <DocumentEvidenceItem
      reference={reference}
      datasetId={datasetId}
      onOpen={open}
      unavailable={unavailable}
    />
  ) : (
    <div className="evidence-capsule evidence-capsule--unavailable">
      <DescriptionOutlined fontSize="small" aria-hidden="true" />
      <span className="evidence-capsule__content">
        <strong title={label}>{label}</strong>
        <span className="evidence-item__note">No linked dataset record</span>
      </span>
      <Tooltip title={reference.description || "No linked dataset record"}>
        <span className="evidence-item__note">Unavailable</span>
      </Tooltip>
    </div>
  );
};

EvidenceItem.propTypes = {
  reference: referenceShape.isRequired,
  citationNumber: PropTypes.number.isRequired,
  onUnavailable: PropTypes.func.isRequired,
};

/** One citation: its capsule, the revision it quoted, and its quote. */
const EvidenceEntry = ({ reference, citationNumber, showQuotes }) => {
  const [triedUnavailable, setTriedUnavailable] = useState(false);
  return (
    <div>
      <EvidenceItem
        reference={reference}
        citationNumber={citationNumber}
        onUnavailable={() => setTriedUnavailable(true)}
      />
      <CitationRevision
        reference={reference}
        showUnavailable={triedUnavailable}
      />
      {showQuotes && reference.quote && (
        <ReferenceQuote reference={reference} />
      )}
    </div>
  );
};

EvidenceEntry.propTypes = {
  reference: referenceShape.isRequired,
  citationNumber: PropTypes.number.isRequired,
  showQuotes: PropTypes.bool,
};

// quote_verified is stamped by the SERVER: true when the quote was found in
// the retrieved text of the cited record, false when it was not, null when no
// verification ran. A quote that could not be found is not evidence, so the
// three states must not look alike.
const QUOTE_VERIFICATION = {
  verified: {
    text: "Quote verified in the cited record",
    color: "#2e7d32",
    Icon: CheckCircleOutline,
  },
  unverified: {
    text: "Quote not found in the cited record",
    color: "#c62828",
    Icon: ErrorOutline,
  },
  unchecked: {
    text: "Quote not verified",
    color: "rgba(0,0,0,.6)",
    Icon: HelpOutline,
  },
};

const quoteVerificationState = (reference) => {
  if (reference.quote_verified == null) return "unchecked";
  return reference.quote_verified ? "verified" : "unverified";
};

const ReferenceQuote = ({ reference }) => {
  const state = quoteVerificationState(reference);
  const { text, color, Icon } = QUOTE_VERIFICATION[state];
  return (
    <blockquote
      className={`llm-references__quote llm-references__quote--${state}`}
      data-testid="llm-reference-quote"
      data-quote-state={state}
      style={{
        margin: "2px 0 8px 28px",
        paddingLeft: 10,
        borderLeft: `3px solid ${color}`,
        fontStyle: "italic",
        fontSize: "0.85rem",
        whiteSpace: "pre-wrap",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          color,
          fontStyle: "normal",
          fontSize: "0.75rem",
        }}
      >
        <Icon fontSize="inherit" aria-hidden="true" />
        {text}
      </span>
      {reference.quote}
    </blockquote>
  );
};

ReferenceQuote.propTypes = {
  reference: referenceShape.isRequired,
};

/** Procedure/source references cited by the LLM for a prediction. */
export const PredictionReferences = ({
  references,
  initiallyExpanded = false,
  showQuotes = false,
}) => {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  return (
    <div
      className={`llm-references ${expanded ? "llm-references--expanded" : ""}`}
      data-testid="llm-references"
    >
      <button
        type="button"
        className="prediction-card__evidence-toggle"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        {expanded
          ? "Hide sources"
          : `Sources (${references.length} ${references.length === 1 ? "reference" : "references"})`}
      </button>
      {expanded && (
        <div className="llm-references__list">
          {references.map((reference, index) => (
            <EvidenceEntry
              // Record ids plus position: the entry holds per-citation state
              // that must not pass to another citation when results refresh.
              key={`${reference.dataset_id}:${reference.sdr_id}:${reference.source_uid}:${index}`}
              reference={reference}
              citationNumber={index + 1}
              showQuotes={showQuotes}
            />
          ))}
        </div>
      )}
    </div>
  );
};

PredictionReferences.propTypes = {
  references: PropTypes.arrayOf(referenceShape).isRequired,
  /** Start open — for surfaces where the evidence IS the content. */
  initiallyExpanded: PropTypes.bool,
  /** Render each reference's verbatim quote under its capsule. */
  showQuotes: PropTypes.bool,
};
